import { useEffect, type FC } from 'react'

import { Input, Select } from '@skytech/ui'

import { useAppContext } from '@/app/index.context'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useSearch, useSearchParamsByKey } from '@/hooks/searchParams'

import {
  setQueryCurrencyAction,
  setQueryPeriodAction,
  setQueryYearAction,
  SetQuerySearchAction,
} from './context/actions.context'
import { DRILL_DOWN_YEAR_OPTIONS, useDrillDownContext } from './context/index.context'
import { getMonthOptions } from './utils'

export type DrillDownFilterProps = {
  currencies: string[]
  hasSearch?: boolean
}

export const DrillDownFilter: FC<DrillDownFilterProps> = ({
  currencies = [],
  hasSearch = false,
}) => {
  const { locale } = useAppContext()
  const translate = useTranslation()
  const { state, dispatch } = useDrillDownContext()

  const [year, setYear] = useSearchParamsByKey('year', state.year)
  const [period, setPeriod] = useSearchParamsByKey('period', state.period)
  const [currency, setCurrency] = useSearchParamsByKey('currency', state.currency)

  const { searchFilter, setSearchFilter } = useSearch()

  if (!year || !period) {
    return null
  }

  useEffect(() => {
    if (currencies.length > 0) {
      const selectedCurrency = currencies.find(item => item === currency)

      if (!selectedCurrency) {
        setCurrency(currencies[0])
        dispatch(setQueryCurrencyAction(currencies[0]))
      }
    }
  }, [currencies, currency, dispatch, setCurrency])

  // -1 to -4 set aside for quarters that might be needed later
  const DRILL_DOWN_PERIOD_OPTIONS = [{ value: '-5', label: translate('So far') }]

  return (
    <div className="drill-down-filter">
      <Select
        id="year"
        value={year}
        onChange={option => {
          setYear(String(option?.value))
          dispatch(setQueryYearAction(String(option?.value)))
        }}
        options={DRILL_DOWN_YEAR_OPTIONS}
      />
      <Select
        id="month"
        value={period}
        onChange={option => {
          setPeriod(String(option?.value))
          dispatch(setQueryPeriodAction(String(option?.value)))
        }}
        options={[...DRILL_DOWN_PERIOD_OPTIONS, ...getMonthOptions(year, locale)]}
      />

      {currencies.length > 1 && (
        <Select
          id="currency"
          value={currency || state.currency}
          onChange={option => {
            setCurrency(String(option?.value))
            dispatch(setQueryCurrencyAction(String(option?.value)))
          }}
          options={currencies.map(currency => ({
            value: currency,
            label: currency,
          }))}
        />
      )}

      {hasSearch && (
        <Input
          id="department-search"
          value={searchFilter || state.search}
          placeholder={translate('Search')}
          onChange={e => {
            setSearchFilter(e.target.value)
            dispatch(SetQuerySearchAction(e.target.value))
          }}
        />
      )}
    </div>
  )
}
